import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, ArrowLeft, Search, Download, ExternalLink, Eye,
  FolderOpen, Star, ChevronRight, BookOpen, DollarSign, Building2,
  ClipboardList, Briefcase, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  payroll: DollarSign,
  worksite: Building2,
  forms: ClipboardList,
  program_info: BookOpen,
  career: Briefcase,
  faq: HelpCircle
};

const ResourceCenterPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/resources/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchResources = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/resources`;
      if (selectedCategory) {
        url += `?category=${selectedCategory}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setResources(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleResourceClick = async (resource) => {
    // Track view
    try {
      await fetch(`${API_URL}/api/resources/${resource.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
    
    // Open resource
    window.open(resource.url, '_blank');
  };

  const filteredResources = resources.filter(r => 
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const featuredResources = filteredResources.filter(r => r.is_featured);
  const regularResources = filteredResources.filter(r => !r.is_featured);

  const getResourceTypeIcon = (type) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'link': return '🔗';
      case 'video': return '🎬';
      case 'document': return '📝';
      default: return '📁';
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Header */}
      <div className="p-6 rounded-b-3xl" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F', color: '#fff' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-3">
              <FolderOpen className="w-10 h-10" style={{ color: colors?.text_on_primary || '#FFFFFF' }} />
              <div>
                <h1 className="font-heading text-3xl font-bold" style={{ color: colors?.text_on_primary || '#FFFFFF' }}>Resource Center</h1>
                <p className="opacity-90" style={{ color: colors?.text_on_primary || '#FFFFFF' }}>Important documents & info</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl bg-white"
            data-testid="search-resources"
          />
        </div>
      </div>

      {/* Category Pills */}
      <div className="max-w-4xl mx-auto px-4 pb-4">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <Button
            variant={selectedCategory === null ? 'default' : 'outline'}
            onClick={() => setSelectedCategory(null)}
            className="rounded-full whitespace-nowrap"
            data-testid="category-all"
          >
            All
          </Button>
          {categories.map(cat => {
            const Icon = categoryIcons[cat.id] || FileText;
            return (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat.id)}
                className="rounded-full whitespace-nowrap gap-2"
                data-testid={`category-${cat.id}`}
              >
                <span>{cat.icon}</span> {cat.name}
                {cat.count > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                    {cat.count}
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
          </div>
        ) : filteredResources.length === 0 ? (
          <Card className="p-8 text-center rounded-3xl">
            <FolderOpen className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No Resources Yet</h2>
            <p className="text-muted-foreground">
              {searchTerm ? 'No resources match your search.' : 'Resources will be added by program administrators.'}
            </p>
          </Card>
        ) : (
          <>
            {/* Featured Resources */}
            {featuredResources.length > 0 && (
              <div>
                <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  Important Documents
                </h2>
                <div className="grid gap-3">
                  {featuredResources.map((resource, index) => (
                    <motion.div
                      key={resource.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card 
                        className="p-4 rounded-2xl cursor-pointer hover:shadow-lg transition-all bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200"
                        onClick={() => handleResourceClick(resource)}
                        data-testid={`resource-${resource.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-2xl">
                            {getResourceTypeIcon(resource.resource_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-gray-900 truncate">{resource.title}</h3>
                              <Badge className="bg-yellow-500 text-white">Featured</Badge>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-1">{resource.description}</p>
                          </div>
                          <ExternalLink className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Sections */}
            {selectedCategory === null ? (
              // Show all categories when no filter selected
              categories.map(category => {
                const categoryResources = regularResources.filter(r => r.category === category.id);
                if (categoryResources.length === 0) return null;
                
                const Icon = categoryIcons[category.id] || FileText;
                
                return (
                  <div key={category.id}>
                    <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                      <span className="text-xl">{category.icon}</span>
                      {category.name}
                    </h2>
                    <div className="grid gap-2">
                      {categoryResources.map((resource, index) => (
                        <ResourceCard 
                          key={resource.id} 
                          resource={resource} 
                          onClick={() => handleResourceClick(resource)}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              // Show filtered resources
              <div className="grid gap-2">
                {regularResources.map((resource, index) => (
                  <ResourceCard 
                    key={resource.id} 
                    resource={resource} 
                    onClick={() => handleResourceClick(resource)}
                    index={index}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Navigation role="youth" />
    </div>
  );
};

const ResourceCard = ({ resource, onClick, index }) => {
  const getResourceTypeIcon = (type) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'link': return '🔗';
      case 'video': return '🎬';
      case 'document': return '📝';
      default: return '📁';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
    >
      <Card 
        className="p-3 rounded-xl cursor-pointer hover:shadow-md transition-all bg-white"
        onClick={onClick}
        data-testid={`resource-${resource.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-xl">
            {getResourceTypeIcon(resource.resource_type)}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 truncate">{resource.title}</h3>
            <p className="text-xs text-gray-500 line-clamp-1">{resource.description}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {resource.views > 0 && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Eye className="w-3 h-3" /> {resource.views}
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default ResourceCenterPage;
